/**
 * @file     resolveContent.ts
 * @purpose  Recursively resolves a content schema by calling i18next t() for every MessageRef and LocalizedTextRef node, producing a fully-translated content tree.
 */
import type { TFunction } from 'i18next';
import { isLocalizedTextRef, isMessageRef } from './messageRef';
import type { LocalizedTextRef, MessageRef, ResolvedContent } from './types';

export const resolveMessage = (t: TFunction, ref: MessageRef) => {
  return t(ref.key, ref.values);
};

export const resolveLocalizedText = (t: TFunction, ref: LocalizedTextRef) => {
  return t(ref.key, {
    defaultValue: ref.defaultValue,
    ...(ref.values ?? {}),
  });
};

const resolveNode = (t: TFunction, value: unknown): unknown => {
  if (isMessageRef(value)) {
    return resolveMessage(t, value);
  }

  if (isLocalizedTextRef(value)) {
    return resolveLocalizedText(t, value);
  }

  if (Array.isArray(value)) {
    return value.map((item) => resolveNode(t, item));
  }

  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, nestedValue]) => [
        key,
        resolveNode(t, nestedValue),
      ]),
    );
  }

  return value;
};

export const resolveContent = <TValue>(
  t: TFunction,
  schema: TValue,
): ResolvedContent<TValue> => {
  return resolveNode(t, schema) as ResolvedContent<TValue>;
};
